/**
 * Bio Template Premium Simple
 *
 * Versión simplificada de BioTemplatePremium con dos botones:
 *   1. Ingreso a Grupo Telegram   → AR: checkout MercadoPago Basic (`VITE_MP_CHECKOUT_BASIC_AR` o legacy
 *                                   `VITE_MP_TELEGRAM_GROUP_LINK`); resto: Stripe Checkout USD "Miembro Basic".
 *                                   Tras el pago → /muchas-gracias (validación + link Telegram).
 *                                   MP o Stripe: **misma** regla de grupo — solo `plan_tier` basic|vip
 *                                   (ver `src/lib/telegramPlanTier.ts` + `api/lib/telegramPlanTier.ts`).
 *   2. Miembros Exclusivos        → video Mux + CTA (AR): checkout MP VIP
 *                                   (`VITE_MP_CHECKOUT_VIP_AR` o legacy `VITE_MP_VIP_LINK`);
 *                                   USD → Stripe Checkout (suscripción VIP en este dominio);
 *                                   fallback → fan/subscripcion?plan=vip.
 *
 * Layout centrado (mobile/desktop), fondo oscuro neutro (zinc/negro), cards discretas.
 * Checkout: AR → MercadoPago (URLs env); internacional → Stripe via `/api/create-subscription-checkout`
 * (el API fuerza `metadata[source]` = `BRAND_SOURCE_KEY` para webhooks).
 *
 * Multi-idioma: es · en · pt · fr · de · it (fallback a inglés)
 * Configurable por país via props (country, language) — igual que BioTemplatePremium.
 *
 * Configuración:
 *   telegramGroupUrl         → prop o env var VITE_TELEGRAM_PRIVATE_GROUP_URL (invite t.me post-pago / éxito)
 *   freeTelegramGroupUrl    → opcional; canal gratis. Si falta → VITE_TELEGRAM_FREE_GROUP_URL
 *   membershipPlaybackId     → prop o env var VITE_PREMIUM_MUX_ES_VIP / VITE_PREMIUM_MUX_EN_VIP
 *   MercadoPago AR (checkout `mercadopago.com…`, no son links t.me):
 *   - Basic: `VITE_MP_CHECKOUT_BASIC_AR` o legacy `VITE_MP_TELEGRAM_GROUP_LINK`
 *   - VIP:   `VITE_MP_CHECKOUT_VIP_AR`   o legacy `VITE_MP_VIP_LINK`
 *
 * Precio y producto Stripe (defaults en código; opcional en Railway):
 *   - VITE_TELEGRAM_BASIC_USD_AMOUNT_CENTS (ej. 699 = $6.99) — también alimenta el precio en la card Telegram (todos los idiomas)
 *   - VITE_TELEGRAM_BASIC_PRODUCT_NAME, VITE_TELEGRAM_BASIC_PLAN_TIER, VITE_TELEGRAM_BASIC_FLOW_NAME
 *
 * Fondo (Railway / build):
 *   - `VITE_PREMIUM_BACKGROUND_IMAGE_URL` — desktop (breakpoint md+); si no hay móvil dedicada, también en phone.
 *   - `VITE_PREMIUM_BACKGROUND_IMAGE_MOBILE_URL` — opcional, solo viewport menor a `md`; si falta, se usa la URL de desktop.
 * Props opcionales: `backgroundImageDesktopUrl`, `backgroundImageMobileUrl` (misma regla de fallback).
 */

import MuxVideoPlayerWithCTA from "@/components/MuxVideoPlayerWithCTA";
import StripeCheckoutOverlay, { type CheckoutConfig } from "@/components/checkout/StripeCheckoutOverlay";
import { buildStripeSuccessReturnUrl } from "@/lib/muchasGraciasSuccessUrl";
import { initConversionTracking } from "@/lib/conversionAnalytics";
import {
  subscribePostHogClientReady,
  registerBioPremiumFunnelContextPostHog,
  getPostHogCorrelationIdsPostHog,
  identifyUserByEmailPostHog,
} from "@/lib/posthogAnalytics";
import { mirrorFunnelEvent } from "@/lib/mirrorFunnelEvent";
import { getPlansForCurrency, getPlaybackIds, getPremiumPlansFunnelSnapshot, getCurrencyForCountry } from "@/config/bioTemplatePremiumPlans";
import {
  getPremiumInvitePaths,
  getPremiumWebsitePaths,
  PREMIUM_INVITE_FLOW_NAME,
  PREMIUM_WEBSITE_FLOW_NAME,
} from "@/config/bioTemplatePremiumFlow";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useSiteSettingsVariant } from "@/hooks/useSiteSettingsVariant";
import { useInternationalHomeVipCheckoutPricing } from "@/hooks/useInternationalHomeVipCheckoutPricing";
import { usePremiumInviteFreeTelegramPublicUrl } from "@/hooks/usePremiumInviteFreeTelegramPublicUrl";
import { resolveMuxFriendlyFunnelOpts } from "@/lib/premiumInviteMuxFunnel";
import { BRAND_FAN_URL, BRAND_SITE_HOST, BRAND_SOURCE_KEY } from "@/lib/brandConfig";
import { normalizeWorkerCountry } from "@/lib/serverTemplateDetection";
import { cn } from "@/lib/utils";
import { Crown, Loader2, Mail, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// ─── Constantes ───────────────────────────────────────────────────────────────

const FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif';

const DEFAULT_SUBSCRIPTION_URL = `${BRAND_FAN_URL}/subscripcion`;

function parseUsdCentsFromEnv(raw: string | undefined, fallback: number): number {
  if (raw === undefined || String(raw).trim() === "") return fallback;
  const n = Number(String(raw).trim());
  if (!Number.isFinite(n)) return fallback;
  const cents = Math.round(n);
  if (cents < 50 || cents > 500_000) return fallback;
  return cents;
}

/** Centavos USD para Stripe; default 699 ($6.99). Override: VITE_TELEGRAM_BASIC_USD_AMOUNT_CENTS en Railway. */
const TELEGRAM_PLAN_AMOUNT_CENTS = parseUsdCentsFromEnv(
  import.meta.env.VITE_TELEGRAM_BASIC_USD_AMOUNT_CENTS,
  699,
);
const TELEGRAM_PLAN_PRODUCT_NAME = (
  import.meta.env.VITE_TELEGRAM_BASIC_PRODUCT_NAME?.trim() || "Miembro Basic"
).slice(0, 120);
const TELEGRAM_PLAN_TIER = (
  import.meta.env.VITE_TELEGRAM_BASIC_PLAN_TIER?.trim() || "basic"
).slice(0, 32);
const TELEGRAM_FLOW_NAME = (
  import.meta.env.VITE_TELEGRAM_BASIC_FLOW_NAME?.trim() || "bio_premium_simple"
).slice(0, 64);

const BIO_SIMPLE_FUNNEL_SID_KEY = "bio_simple_funnel_sid_v1";

/** Sesión estable por pestaña (survive refresh) para agrupar CTAs en admin / Slack. */
function getOrCreateBioSimpleSessionId(): string {
  try {
    const existing = sessionStorage.getItem(BIO_SIMPLE_FUNNEL_SID_KEY);
    if (existing && existing.length >= 8) return existing;
    const id =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `bps_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
    sessionStorage.setItem(BIO_SIMPLE_FUNNEL_SID_KEY, id);
    return id;
  } catch {
    return typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `bps_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
  }
}

function normalizeVisitorEmail(raw: string | undefined | null): string | undefined {
  const t = typeof raw === "string" ? raw.trim().toLowerCase() : "";
  if (!t || !t.includes("@")) return undefined;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t)) return undefined;
  return t.slice(0, 254);
}

/**
 * Cola del subtítulo con precio USD/mes según `VITE_TELEGRAM_BASIC_USD_AMOUNT_CENTS` (misma fuente que el checkout).
 */
function telegramPriceTailUsd(cents: number, langKey: string): string {
  const n = cents / 100;
  const fmt = (locale: string) =>
    new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  switch (langKey) {
    case "es":
      return `${fmt("es-AR")} USD/mes`;
    case "pt":
      return `${fmt("pt-BR")} USD/mês`;
    case "fr":
      return `${fmt("fr-FR")} USD/mois`;
    case "de":
      return `${fmt("de-DE")} USD/Monat`;
    case "it":
      return `${fmt("it-IT")} USD/mese`;
    default:
      return `${fmt("en-US")} USD/mo`;
  }
}

/** ── MercadoPago solo Argentina: URLs de *checkout* MP (no son invites t.me; esos son VITE_TELEGRAM_*) ── */
function mpEnvTrim(...candidates: (string | undefined)[]): string {
  for (const c of candidates) {
    const t = typeof c === "string" ? c.trim() : "";
    if (t) return t;
  }
  return "";
}

/**
 * URL del canal/grupo Telegram gratis (card 3). Solo env `VITE_TELEGRAM_FREE_GROUP_URL` u override por prop;
 * debe ser `http:` o `https:` (p. ej. `https://t.me/+…`). Cualquier otro esquema → se ignora (card oculta).
 */
function resolveTelegramFreeGroupUrl(...candidates: (string | undefined)[]): string {
  const raw = mpEnvTrim(...candidates);
  if (!raw) return "";
  try {
    const u = new URL(raw);
    if (u.protocol !== "http:" && u.protocol !== "https:") return "";
    return raw;
  } catch {
    return "";
  }
}

/** ── Links Stripe/Fan (Internacional) ─────────────────────────────────────── */
// VIP ya está manejado por el subdominio fan
const MEMBERSHIP_BASE_URL = `${BRAND_FAN_URL}/subscripcion`;

/** Icono SVG de Telegram (paper plane) */
const TELEGRAM_SVG_PATH =
  "M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z";

// ─── Labels multi-idioma ──────────────────────────────────────────────────────

interface SimpleLabels {
  badge: string;
  telegramCardTitle: string;
  telegramCta: string;
  telegramLoading: string;
  telegramError: string;
  /** AR: falta checkout MP Basic — ver `VITE_MP_CHECKOUT_BASIC_AR` (o legacy `VITE_MP_TELEGRAM_GROUP_LINK`) */
  telegramMpLinkMissing: string;
  membershipCardTitle: string;
  membershipCardSubtitle: string;
  membershipCta: string;
  /** Texto del botón CTA dentro del video de Miembros Exclusivos */
  membershipVideoCta: string;
  /** Línea sobre el botón en el reproductor Mux: chat privado VIP */
  membershipVideoCtaSubtitle: string;
  /** Tercera card — Free Telegram (canal gratis sin pago). Sólo se renderiza si hay URL. */
  freeTelegramCardTitle: string;
  freeTelegramCardSubtitle: string;
  freeTelegramCta: string;
  /** Modal de email */
  emailModalTitle: string;
  emailModalSubtitle: string;
  emailPlaceholder: string;
  emailContinue: string;
  emailCancel: string;
  emailInvalid: string;
  emailRequired: string;
  emailSecurityNote: string;
  /** Pantalla inicial mientras decodifica el fondo (evita “flash” en foto grande). */
  pageBootstrapping: string;
}

const LABELS: Record<string, SimpleLabels> = {
  es: {
    badge: "Únete",
    telegramCardTitle: "Telegram privado",
    telegramCta: "Ingreso a Grupo Telegram",
    telegramLoading: "Preparando pago...",
    telegramError: "Error al iniciar el pago. Intentá de nuevo.",
    telegramMpLinkMissing:
      "Falta el checkout de MercadoPago (plan Basic) para Argentina. Configurá VITE_MP_CHECKOUT_BASIC_AR o VITE_MP_TELEGRAM_GROUP_LINK (legacy).",
    membershipCardTitle: "Zona VIP",
    membershipCardSubtitle: "Chat privado para hablar y ver 💬👀✨",
    membershipCta: "Miembros Exclusivos",
    membershipVideoCta: "Miembros Exclusivos",
    membershipVideoCtaSubtitle: "El chat VIP es para hablar y ver en privado 💬👀✨",
    freeTelegramCardTitle: "Telegram Gratis",
    freeTelegramCardSubtitle: "Canal abierto · novedades sin costo",
    freeTelegramCta: "Unirme al canal gratis",
    emailModalTitle: "Revisá tu email",
    emailModalSubtitle:
      "Ese correo no es válido. Corregilo, o borrá el campo, cerrá esta ventana y tocá de nuevo: Stripe te lo pedirá al pagar.",
    emailPlaceholder: "tu@email.com",
    emailContinue: "Continuar al pago",
    emailCancel: "Cancelar",
    emailInvalid: "Por favor ingresá un email válido",
    emailRequired: "El email es requerido",
    emailSecurityNote:
      "Si lo cargás acá, lo mandamos a Stripe para acelerar el checkout. Si no, Stripe lo pide en su pantalla.",
    pageBootstrapping: "Cargando…",
  },
  en: {
    badge: "Join",
    telegramCardTitle: "Private Telegram",
    telegramCta: "Join Telegram Group",
    telegramLoading: "Preparing payment...",
    telegramError: "Error starting payment. Please try again.",
    telegramMpLinkMissing:
      "Argentina MercadoPago checkout (Basic) is missing. Set VITE_MP_CHECKOUT_BASIC_AR or legacy VITE_MP_TELEGRAM_GROUP_LINK.",
    membershipCardTitle: "VIP corner",
    membershipCardSubtitle: "Private chat to talk and see 💬👀✨",
    membershipCta: "Exclusive Members",
    membershipVideoCta: "Exclusive Members",
    membershipVideoCtaSubtitle: "VIP private chat: talk and see there 💬👀✨",
    freeTelegramCardTitle: "Free Telegram",
    freeTelegramCardSubtitle: "Open channel · free updates",
    freeTelegramCta: "Join free channel",
    emailModalTitle: "Check your email",
    emailModalSubtitle:
      "That address isn’t valid. Fix it, or clear the field, close this, and tap again — Stripe will ask at checkout.",
    emailPlaceholder: "your@email.com",
    emailContinue: "Continue to payment",
    emailCancel: "Cancel",
    emailInvalid: "Please enter a valid email",
    emailRequired: "Email is required",
    emailSecurityNote:
      "If you enter it here, we send it to Stripe to speed up checkout. Otherwise Stripe asks on its page.",
    pageBootstrapping: "Loading…",
  },
  pt: {
    badge: "Entrar",
    telegramCardTitle: "Telegram privado",
    telegramCta: "Entrar no Grupo Telegram",
    telegramLoading: "Preparando pagamento...",
    telegramError: "Erro ao iniciar o pagamento. Tente novamente.",
    telegramMpLinkMissing:
      "Checkout Mercado Pago (Basic) para Argentina ausente. Configure VITE_MP_CHECKOUT_BASIC_AR ou VITE_MP_TELEGRAM_GROUP_LINK.",
    membershipCardTitle: "Cantinho VIP",
    membershipCardSubtitle: "Chat privado pra conversar e ver 💬👀✨",
    membershipCta: "Membros Exclusivos",
    membershipVideoCta: "Membros Exclusivos",
    membershipVideoCtaSubtitle: "No chat VIP: conversar e ver em privado 💬👀✨",
    freeTelegramCardTitle: "Telegram Grátis",
    freeTelegramCardSubtitle: "Canal aberto · novidades sem custo",
    freeTelegramCta: "Entrar no canal grátis",
    emailModalTitle: "Confira seu email",
    emailModalSubtitle:
      "Esse email não é válido. Corrija, ou apague o campo, feche e toque de novo — o Stripe pedirá no pagamento.",
    emailPlaceholder: "seu@email.com",
    emailContinue: "Continuar para o pagamento",
    emailCancel: "Cancelar",
    emailInvalid: "Por favor, digite um email válido",
    emailRequired: "Email é obrigatório",
    emailSecurityNote:
      "Se preencher aqui, enviamos ao Stripe para agilizar. Se não, o Stripe pede na página de pagamento.",
    pageBootstrapping: "Carregando…",
  },
  fr: {
    badge: "Rejoindre",
    telegramCardTitle: "Telegram privé",
    telegramCta: "Rejoindre le Groupe Telegram",
    telegramLoading: "Préparation du paiement...",
    telegramError: "Erreur lors du démarrage du paiement. Réessayez.",
    telegramMpLinkMissing:
      "Checkout Mercado Pago (Basic) Argentine manquant. Définissez VITE_MP_CHECKOUT_BASIC_AR ou VITE_MP_TELEGRAM_GROUP_LINK.",
    membershipCardTitle: "Espace VIP",
    membershipCardSubtitle: "Chat privé pour parler et voir 💬👀✨",
    membershipCta: "Membres Exclusifs",
    membershipVideoCta: "Membres Exclusifs",
    membershipVideoCtaSubtitle: "Le chat VIP : parler et voir en privé 💬👀✨",
    freeTelegramCardTitle: "Telegram Gratuit",
    freeTelegramCardSubtitle: "Canal ouvert · actualités gratuites",
    freeTelegramCta: "Rejoindre le canal gratuit",
    emailModalTitle: "Vérifiez votre email",
    emailModalSubtitle:
      "Adresse invalide. Corrigez-la, ou videz le champ, fermez et touchez à nouveau — Stripe demandera au paiement.",
    emailPlaceholder: "votre@email.com",
    emailContinue: "Continuer vers le paiement",
    emailCancel: "Annuler",
    emailInvalid: "Veuillez entrer un email valide",
    emailRequired: "L'email est requis",
    emailSecurityNote:
      "Si vous le saisissez ici, nous l’envoyons à Stripe pour accélérer. Sinon Stripe le demande sur sa page.",
    pageBootstrapping: "Chargement…",
  },
  de: {
    badge: "Beitreten",
    telegramCardTitle: "Privates Telegram",
    telegramCta: "Telegram-Gruppe beitreten",
    telegramLoading: "Zahlung wird vorbereitet...",
    telegramError: "Fehler beim Starten der Zahlung. Bitte versuche es erneut.",
    telegramMpLinkMissing:
      "Mercado-Pago-Checkout (Basic) für Argentinien fehlt. VITE_MP_CHECKOUT_BASIC_AR oder VITE_MP_TELEGRAM_GROUP_LINK setzen.",
    membershipCardTitle: "VIP-Bereich",
    membershipCardSubtitle: "Privater Chat zum Reden und Sehen 💬👀✨",
    membershipCta: "Exklusive Mitglieder",
    membershipVideoCta: "Exklusive Mitglieder",
    membershipVideoCtaSubtitle: "Im VIP-Privatchat: reden und sehen 💬👀✨",
    freeTelegramCardTitle: "Kostenloses Telegram",
    freeTelegramCardSubtitle: "Offener Kanal · kostenlose Updates",
    freeTelegramCta: "Kostenlosem Kanal beitreten",
    emailModalTitle: "E-Mail prüfen",
    emailModalSubtitle:
      "Ungültige Adresse. Korrigieren oder Feld leeren, schließen und erneut tippen — Stripe fragt beim Bezahlen.",
    emailPlaceholder: "ihre@email.com",
    emailContinue: "Weiter zur Zahlung",
    emailCancel: "Abbrechen",
    emailInvalid: "Bitte geben Sie eine gültige E-Mail ein",
    emailRequired: "E-Mail ist erforderlich",
    emailSecurityNote:
      "Wenn Sie sie hier eintragen, senden wir sie an Stripe. Sonst fragt Stripe auf seiner Seite.",
    pageBootstrapping: "Wird geladen…",
  },
  it: {
    badge: "Unisciti",
    telegramCardTitle: "Telegram privato",
    telegramCta: "Entra nel Gruppo Telegram",
    telegramLoading: "Preparazione del pagamento...",
    telegramError: "Errore nell'avvio del pagamento. Riprova.",
    telegramMpLinkMissing:
      "Checkout Mercado Pago (Basic) per Argentina mancante. Imposta VITE_MP_CHECKOUT_BASIC_AR o VITE_MP_TELEGRAM_GROUP_LINK.",
    membershipCardTitle: "Angolo VIP",
    membershipCardSubtitle: "Chat privata per parlare e vedere 💬👀✨",
    membershipCta: "Membri Esclusivi",
    membershipVideoCta: "Membri Esclusivi",
    membershipVideoCtaSubtitle: "Nel chat VIP: parlare e vedere in privato 💬👀✨",
    freeTelegramCardTitle: "Telegram Gratis",
    freeTelegramCardSubtitle: "Canale aperto · novità gratuite",
    freeTelegramCta: "Unisciti al canale gratis",
    emailModalTitle: "Controlla l’email",
    emailModalSubtitle:
      "Indirizzo non valido. Correggi, svuota il campo, chiudi e tocca di nuovo — Stripe chiederà al pagamento.",
    emailPlaceholder: "tua@email.com",
    emailContinue: "Continua al pagamento",
    emailCancel: "Annulla",
    emailInvalid: "Inserisci un'email valida",
    emailRequired: "L'email è obbligatoria",
    emailSecurityNote:
      "Se la inserisci qui, la inviamo a Stripe per velocizzare. Altrimenti Stripe la chiede nella sua pagina.",
    pageBootstrapping: "Caricamento…",
  },
};

function getLabels(language: string): SimpleLabels {
  const lang = language.split("-")[0].toLowerCase();
  // Idiomas con copy completo en LABELS; el resto cae a inglés (mejor que mezclar vacío).
  return LABELS[lang] ?? LABELS["en"];
}

/** URL de fondo: solo https permitido (misma validación que antes). */
function resolveHttpsBackgroundUrl(raw: string | undefined): string | undefined {
  const t = raw?.trim();
  if (!t || !t.startsWith("http")) return undefined;
  return t;
}

/**
 * Una línea corta bajo el título Telegram: precio USD/mes desde env. Sin
 * referencia a Mercado Pago — política Stripe-only para todos los países
 * (ver `src/lib/paymentProviderPolicy.ts`).
 */
function getTelegramSubtitle(_country: string, langShort: string): string {
  const priceLang = ["es", "pt", "fr", "de", "it"].includes(langShort)
    ? langShort
    : "en";
  return telegramPriceTailUsd(TELEGRAM_PLAN_AMOUNT_CENTS, priceLang);
}

// ─── Tipos internos ───────────────────────────────────────────────────────────

type CheckoutState = "idle" | "loading" | "error";

// ─── Props ────────────────────────────────────────────────────────────────────

export interface BioTemplatePremiumSimpleProps {
  /**
   * Código de idioma del usuario (ej: "es-AR", "en-US", "pt-BR").
   * Controla todos los textos de la UI. Fallback a inglés.
   */
  language?: string;

  /**
   * Código de país ISO (ej: "AR", "MX", "BR").
   * Se pasa al checkout de Stripe como metadata.
   */
  country?: string;

  /**
   * URL del grupo privado de Telegram.
   * Se pasa a la página de éxito como query param para que el usuario la reciba
   * tras completar el pago. Fallback: env var VITE_TELEGRAM_PRIVATE_GROUP_URL.
   * Ej: "https://t.me/+AbCdEfGhIjKlMnO"
   */
  telegramGroupUrl?: string;

  /**
   * Canal/grupo Telegram **gratis** (card “Telegram gratis”). Solo enlace directo, sin checkout.
   * Si se omite, se usa `VITE_TELEGRAM_FREE_GROUP_URL`. Prop tiene prioridad sobre la env var.
   */
  freeTelegramGroupUrl?: string;

  /**
   * Mux playback ID del video que se muestra al hacer clic en "Miembros Exclusivos".
   * Si no se pasa, se lee del env var correspondiente al idioma
   * (VITE_PREMIUM_MUX_ES_VIP para español, VITE_PREMIUM_MUX_EN_VIP para inglés).
   */
  membershipPlaybackId?: string;

  /**
   * Imagen de fondo desktop (Tailwind `md:` y superior). Override de `VITE_PREMIUM_BACKGROUND_IMAGE_URL`.
   */
  backgroundImageDesktopUrl?: string;
  /**
   * Imagen de fondo móvil (viewport menor al breakpoint `md` de Tailwind). Override de `VITE_PREMIUM_BACKGROUND_IMAGE_MOBILE_URL`;
   * si no hay URL móvil válida, se usa la de desktop.
   */
  backgroundImageMobileUrl?: string;

  /**
   * Más overlay oscuro sobre la foto (p. ej. `/invitacion-premium` full-bleed) sin cambiar la home.
   */
  deepBackgroundScrim?: boolean;

  /**
   * Admin-generated invite code that gated access to this page.
   * Passed to the checkout API so it can reject the session if the invite expired.
   */
  inviteCode?: string;
}

// ─── Componente ───────────────────────────────────────────────────────────────

export default function BioTemplatePremiumSimple({
  language = "es",
  country = "AR",
  telegramGroupUrl,
  freeTelegramGroupUrl,
  membershipPlaybackId,
  backgroundImageDesktopUrl,
  backgroundImageMobileUrl,
  deepBackgroundScrim = false,
  inviteCode,
}: BioTemplatePremiumSimpleProps) {
  // País ISO2 coherente con Worker/cookies (evita "ar" vs "AR" y ramas MP/Stripe incorrectas).
  const countryCode = normalizeWorkerCountry(country);
  // AR + MercadoPago: la UI debe ser en español aunque el Worker mande en-US (VPN, cookie, etc.).
  const uiLanguage = countryCode === "AR" ? "es-AR" : language;
  const L = getLabels(uiLanguage);
  const lang = uiLanguage.split("-")[0] as "en" | "es" | "de" | "fr" | "it" | "pt" | "nl";
  const { vpnDetected, asOrganization } = useTemplateSelection();

  // Admin VIP (country-templates / vipPricingDefault) — same source as DualCTA.
  const siteSettingsVariant = useSiteSettingsVariant();
  const { settings: siteSettings } = useSiteSettings(siteSettingsVariant);
  const { pricing: adminVipPricing } = useInternationalHomeVipCheckoutPricing({
    country: countryCode,
    vipPricingByCountry: siteSettings.internationalHomeVipPricingByCountry,
    vipPricingDefault: siteSettings.internationalHomeVipPricingDefault,
    runtimeVipPriceUsd: null,
    language: uiLanguage,
  });

  // Config remota del admin (Premium Invite): activar/desactivar cards +
  // embudo "friendly" del video Mi VIP (CTA → Telegram gratis).
  const { publicConfig, url: adminFreeTelegramUrl } =
    usePremiumInviteFreeTelegramPublicUrl(true, countryCode);

  // Toggles admin `mux_cta_friendly_banner` / `mux_cta_free_join_copy`
  // (null = heredar env Railway). Controla cartel "no es VIP" + copy free-join.
  const muxFunnelOpts = useMemo(
    () => resolveMuxFriendlyFunnelOpts(publicConfig),
    [publicConfig],
  );

  // Monto del plan Telegram Basic en centavos USD, editable desde admin
  // (`telegram_basic_price_cents`). null = heredar env/default. Se usa dentro
  // del componente; el helper module-level `getTelegramSubtitle` sigue usando
  // el const de env (no tiene acceso a publicConfig).
  const telegramPlanAmountCents =
    publicConfig?.telegram_basic_price_cents ?? TELEGRAM_PLAN_AMOUNT_CENTS;

  // Preview desde el admin: ?preview_show_tg=0 / ?preview_show_vip=0 fuerzan ocultar.
  const previewCardOverrides = useMemo(() => {
    if (typeof window === "undefined") return { tg: null as boolean | null, vip: null as boolean | null };
    const sp = new URLSearchParams(window.location.search);
    const read = (k: string): boolean | null => (sp.has(k) ? sp.get(k) !== "0" : null);
    return { tg: read("preview_show_tg"), vip: read("preview_show_vip") };
  }, []);

  // null/true en config = mostrar (heredar default); solo `false` oculta la card.
  const showTelegramCard =
    previewCardOverrides.tg ?? publicConfig?.simple_show_telegram_card !== false;
  const showVipCard =
    previewCardOverrides.vip ?? publicConfig?.simple_show_vip_card !== false;

  const telegramSubtitle = getTelegramSubtitle(countryCode, lang);

  const [checkoutState, setCheckoutState] = useState<CheckoutState>("idle");
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [showMembershipVideo, setShowMembershipVideo] = useState(false);
  // Capa de carga amable del overlay Mux: muestra poster + spinner hasta que
  // el video realmente arranca (onPlay) o, como red de seguridad, onReady/timeout.
  const [membershipVideoReady, setMembershipVideoReady] = useState(false);
  useEffect(() => {
    if (!showMembershipVideo) {
      setMembershipVideoReady(false);
      return;
    }
    // Safety: si onPlay/onReady no llegan (red lenta, autoplay bloqueado),
    // ocultamos el loader igual para no dejarlo pegado.
    const t = window.setTimeout(() => setMembershipVideoReady(true), 6000);
    return () => window.clearTimeout(t);
  }, [showMembershipVideo]);
  
  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  // Embedded Stripe checkout (international flows). When non-null the overlay
  // opens with this config; clearing it closes the overlay. AR users go
  // to MercadoPago via redirect and never set this state.
  const [stripeCheckoutConfig, setStripeCheckoutConfig] = useState<CheckoutConfig | null>(null);
  const lastBioSimpleViewKey = useRef<string>("");
  // Sesión funnel por pestaña (sessionStorage) — mismo visitante tras refresh;
  // agrupa CTAs en /admin/live-funnel y en Slack.
  const sessionIdRef = useRef<string>("");
  if (!sessionIdRef.current) {
    sessionIdRef.current = getOrCreateBioSimpleSessionId();
  }
  const simpleFlowTraceRef = useRef(
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `bps_visit_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
  );

  useEffect(() => {
    return subscribePostHogClientReady(() => {
      registerBioPremiumFunnelContextPostHog({
        bio_premium_flow_trace_id: simpleFlowTraceRef.current,
        bio_premium_tab_session_id: sessionIdRef.current,
        template_funnel: TELEGRAM_FLOW_NAME,
      });
    });
  }, []);

  // ── Membresía VIP: Stripe-only (USD); fan subdomain como fallback. MercadoPago deshabilitado. ──
  const fanVipFallbackUrl = useMemo(
    () => `${MEMBERSHIP_BASE_URL}?plan=vip&country=${encodeURIComponent(countryCode)}`,
    [countryCode],
  );
  /** Destino VIP de pago: ancla same-origin (el click lo maneja `navigateMembershipVipCheckout`). */
  const membershipVipCheckoutCtaUrl = "/#vip-membership";

  // Playback ID del video de membresía: prop → env var según idioma → fallback
  const resolvedMembershipPlaybackId =
    membershipPlaybackId?.trim() || getPlaybackIds(uiLanguage).vip || "";

  // URL del grupo de Telegram: prop → env var → vacío
  const resolvedTelegramUrl =
    telegramGroupUrl?.trim() ||
    (import.meta.env.VITE_TELEGRAM_PRIVATE_GROUP_URL as string | undefined)?.trim() ||
    "";

  /**
   * Card “Telegram gratis”: prop → admin (global / by-country) → env free group.
   * Misma cadena que DualCTA / resolveFreeJoinTelegramUrl.
   */
  const resolvedFreeTelegramUrl = useMemo(
    () =>
      // Toggle maestro del "Elemento free-telegram" (admin) OFF, o Argentina: sin free-telegram
      // en ninguna superficie (card gratis ni video Mux "Mi VIP"). Va directo a Stripe; oculta la
      // card, neutraliza el handler y la telemetría queda coherente.
      countryCode === "AR" || publicConfig?.free_telegram_enabled === false
        ? ""
        : resolveTelegramFreeGroupUrl(
            freeTelegramGroupUrl,
            adminFreeTelegramUrl,
            import.meta.env.VITE_TELEGRAM_FREE_GROUP_URL as string | undefined,
          ),
    [countryCode, publicConfig?.free_telegram_enabled, freeTelegramGroupUrl, adminFreeTelegramUrl],
  );

  /**
   * Embudo "friendly": cuando el admin activa `mux_cta_free_join_copy` y hay
   * URL de Telegram gratis (admin `free_telegram_url` o env), el CTA del video
   * Mi VIP abre ese canal gratis en vez del checkout VIP.
   */
  const vipVideoFriendlyFreeDestination = useMemo(() => {
    // Argentina: sin free-join en el video Mi VIP. El CTA del video Mux va directo a
    // Stripe (membershipVipCheckoutCtaUrl), igual criterio que MuxVideoPlayerWithCTA / AR.
    if (countryCode === "AR") return "";
    if (!muxFunnelOpts.freeJoinCopy) return "";
    const u = (adminFreeTelegramUrl || resolvedFreeTelegramUrl || "").trim();
    return /^https?:\/\//i.test(u) ? u : "";
  }, [countryCode, muxFunnelOpts.freeJoinCopy, adminFreeTelegramUrl, resolvedFreeTelegramUrl]);

  /** CTA del video Mi VIP: Telegram gratis (embudo friendly) o checkout VIP. */
  const membershipMuxCtaUrl =
    vipVideoFriendlyFreeDestination || membershipVipCheckoutCtaUrl;

  // Thumbnail del video de membresía (Mux) — se muestra en la card y se precarga
  // time=2: el frame 0 suele ser negro/empalmado; 2s da un poster real → preview
  // más amable en la card y como fondo del overlay mientras carga el video.
  const membershipThumbnailUrl = resolvedMembershipPlaybackId
    ? `https://image.mux.com/${resolvedMembershipPlaybackId}/thumbnail.webp?time=2&width=640&fit_mode=smartcrop&quality=80`
    : null;

  // ── PostHog + server-side mirror tracking ────────────────────────────────
  const captureEvent = (event: string, props?: Record<string, unknown>) => {
    if (typeof window === "undefined") return;
    const ph = (
      window as unknown as {
        posthog?: {
          get_distinct_id?: () => string;
        };
      }
    ).posthog;
    const pagePath = window.location.pathname;
    const PREMIUM_INVITE_SLUGS = ["/acceso", "/unete", "/vip", "/invitacion-premium", "/invitacion-exclusive", "/invitacion-exclusiva", "/invitacion"];
    const landingSurface = PREMIUM_INVITE_SLUGS.some((p) => pagePath === p || pagePath.startsWith(p + "/"))
      ? "invitacion_premium"
      : "homepage";

    const snap = getPremiumPlansFunnelSnapshot({
      currency: getCurrencyForCountry(countryCode),
      language: uiLanguage,
    });
    const corr = getPostHogCorrelationIdsPostHog();
    const visitorEmail = normalizeVisitorEmail(userEmail);
    const identityProps = visitorEmail
      ? {
          visitor_email: visitorEmail,
          email: visitorEmail,
          buyer_email: visitorEmail,
          visitor_identified: true,
        }
      : { visitor_identified: false };

    const fullProps = {
      ...snap,
      flow_trace_id: props?.flow_trace_id ?? simpleFlowTraceRef.current,
      premium_visit_flow_trace_id: simpleFlowTraceRef.current,
      bio_premium_tab_session_id: sessionIdRef.current,
      funnel_tab_session_id: sessionIdRef.current,
      ...corr,
      ...identityProps,
      flow: TELEGRAM_FLOW_NAME,
      plan_tier: TELEGRAM_PLAN_TIER,
      country: countryCode,
      language: uiLanguage,
      language_worker: language,
      template_type: "BioTemplatePremiumSimple",
      vpn_detected: vpnDetected,
      page_path: pagePath,
      page_url: window.location.href,
      landing_surface: landingSurface,
      ...(asOrganization ? { as_organization: asOrganization } : {}),
      ...props,
    };

    mirrorFunnelEvent({
      event_name: event,
      posthog_distinct_id: ph?.get_distinct_id?.(),
      session_id: sessionIdRef.current,
      customer_email: visitorEmail,
      template: "BioTemplatePremiumSimple",
      template_variant: countryCode === "AR" ? "ar_direct" : "intl_stripe",
      flow_name: TELEGRAM_FLOW_NAME,
      // Differentiates /acceso vs /unete vs /vip vs /invitacion-premium —
      // they all render this same template but represent distinct AR
      // campaigns (Stories vs DMs vs bio link). Without this the admin
      // can't compare CTR per entry point.
      landing_path: pagePath,
      country: countryCode,
      language: uiLanguage,
      page_url: window.location.href,
      props: fullProps,
    });
  };

  const navigateMembershipVipCheckout = useCallback(async () => {
    if (typeof window === "undefined") return;

    setShowMembershipVideo(false);

    // Stripe-only para membresías VIP. La rama AR → MercadoPago quedó deshabilitada;
    // AR cae directamente al mismo flujo Stripe embebido que el resto del mundo.

    // Display/charge: admin VIP (country-templates) wins over premium-invite env.
    const { vipPlan } = getPlansForCurrency("USD", uiLanguage, {
      standardUsd: publicConfig?.standard_price_usd,
      standardArs: publicConfig?.standard_price_ars,
      vipUsd: adminVipPricing.fullPriceUsd,
      vipArs: publicConfig?.vip_price_ars,
    });
    if (vipPlan.isDisabled) {
      // Drop-off: international user wanted VIP but the plan is disabled in config
      captureEvent("bio_simple_vip_fallback", {
        country: countryCode,
        reason: "vip_plan_disabled",
        destination: "fan_fallback",
        join_intent: "vip_membership",
        cta_surface: "vip_membership_card",
      });
      window.location.assign(fanVipFallbackUrl);
      return;
    }

    const _invitePaths = ["/acceso", "/unete", "/vip", "/invitacion-premium", "/invitacion-exclusive", "/invitacion-exclusiva", "/invitacion"];
    const onInvitePage = _invitePaths.some(
      (p) => window.location.pathname === p || window.location.pathname.startsWith(p + "/"),
    );
    const flowName = onInvitePage ? PREMIUM_INVITE_FLOW_NAME : PREMIUM_WEBSITE_FLOW_NAME;
    const paths = onInvitePage ? getPremiumInvitePaths("USD") : getPremiumWebsitePaths("USD");
    const origin = window.location.origin;
    const successUrl = `${origin}${paths.successPath}`;
    const cancelRaw = paths.cancelPath ?? "/";
    const cancelUrl = `${origin}${cancelRaw.startsWith("/") ? cancelRaw : `/${cancelRaw}`}`;
    const flow_trace_id =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `bio_simple_vip_${Date.now()}`;
    // First-month charge cents for UI/analytics (Stripe Price+promo is authority).
    const amountCents =
      adminVipPricing.amountCents > 0
        ? adminVipPricing.amountCents
        : Math.round(vipPlan.price * 100);
    const listCents =
      adminVipPricing.fullPriceCents > 0
        ? adminVipPricing.fullPriceCents
        : Math.round(adminVipPricing.fullPriceUsd * 100);

    const vipBuyerEmail = normalizeVisitorEmail(userEmail);

    captureEvent("bio_simple_vip_checkout_click", {
      country: countryCode,
      gateway: "stripe",
      flow_name: flowName,
      flow_trace_id,
      amount_cents: amountCents,
      list_amount_cents: listCents,
      stripe_price_id: adminVipPricing.stripePriceId || undefined,
      promotion_code_id: adminVipPricing.promotionCodeId || undefined,
      price_source: adminVipPricing.priceSource,
      currency_country_derived: true,
      funnel_step: "opening_embedded_checkout",
      ui_mode: "embedded",
      join_intent: "vip_membership",
      cta_surface: "vip_membership_card",
    });

    // Same charge path as DualCTA: Price ID + optional promo from admin VIP.
    setStripeCheckoutConfig({
      amountCents,
      originalAmountCents: listCents > amountCents ? listCents : undefined,
      discountPercentage:
        adminVipPricing.discountPct > 0 ? adminVipPricing.discountPct : undefined,
      productName: vipPlan.productName,
      country: countryCode,
      buyerEmail: vipBuyerEmail,
      returnUrl: successUrl,
      intervalCount: 1,
      inviteCode: inviteCode || undefined,
      stripePriceId: adminVipPricing.stripePriceId || undefined,
      priceId: adminVipPricing.stripePriceId || undefined,
      promotionCodeId: adminVipPricing.promotionCodeId || undefined,
      useDefaultPromotion: false,
      metadata: {
        source: BRAND_SOURCE_KEY,
        flow: flowName,
        flow_trace_id,
        pack_id: `${flowName}_vip`,
        plan_tier: "vip",
        product_type: "exclusive",
        product_name: vipPlan.productName,
        billing_interval: "month",
        interval_count: "1",
        template: "BioTemplatePremiumSimple",
        template_variant: "vip_subscription",
        price_source: adminVipPricing.priceSource,
        checkout_started_at: new Date().toISOString(),
        ...(vipBuyerEmail ? { buyer_email: vipBuyerEmail } : {}),
        ...(adminVipPricing.stripePriceId
          ? { stripe_price_id: adminVipPricing.stripePriceId }
          : {}),
        ...(adminVipPricing.promotionCodeId
          ? { promotion_code_id: adminVipPricing.promotionCodeId }
          : {}),
      },
    });
  // captureEvent is stable (not memoized) but reads from refs/state by closure;
  // intentionally excluded from deps to avoid re-creating this callback on every render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode, uiLanguage, fanVipFallbackUrl, userEmail, adminVipPricing, publicConfig]);

  // Tracking: vista del template (se re-ejecuta cuando llega el país/URLs reales del Worker)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get("email");
    const viewKey = `${countryCode}|${language}|${resolvedTelegramUrl}|${resolvedMembershipPlaybackId}|${resolvedFreeTelegramUrl}|${emailParam ?? ""}`;
    if (lastBioSimpleViewKey.current !== viewKey) {
      lastBioSimpleViewKey.current = viewKey;
      const emailFromQuery = emailParam && emailParam.includes("@") ? emailParam.trim() : undefined;

      // Map landing_path → human label so #anaafernandez can instantly
      // segment the source at a glance in Slack:
      //   /acceso          → bio_instagram   (link en bio de Instagram)
      //   /unete           → stories         (Story con link)
      //   /vip             → dms             (link en DMs directos)
      //   /invitacion-premium → premium_invite (link canónico de invitación)
      //   otras rutas      → organic
      const landingSurfaceMap: Record<string, string> = {
        "/acceso": "bio_instagram",
        "/unete": "stories",
        "/vip": "dms",
        "/invitacion-premium": "premium_invite",
        "/invitacion-exclusive": "premium_invite",
        "/invitacion-exclusiva": "premium_invite",
        "/invitacion": "premium_invite",
      };
      const pathname = typeof window !== "undefined" ? window.location.pathname : "/";
      const landingSurface =
        Object.entries(landingSurfaceMap).find(([p]) => pathname === p || pathname.startsWith(p + "/"))?.[1]
        ?? "organic";

      const baseViewProps = {
        has_telegram_url: Boolean(resolvedTelegramUrl),
        has_free_telegram_url: Boolean(resolvedFreeTelegramUrl),
        has_video: Boolean(resolvedMembershipPlaybackId),
        landing_surface: landingSurface,
        landing_label: landingSurface, // shown in Slack interesting fields
      };
      if (emailFromQuery) {
        setUserEmail(emailFromQuery);
        identifyUserByEmailPostHog(emailFromQuery, {
          template: "BioTemplatePremiumSimple",
          flow: TELEGRAM_FLOW_NAME,
          landing_path: pathname,
          source: "recovery_email_query",
        });
        captureEvent("bio_simple_view", {
          ...baseViewProps,
          email_prefilled: true,
          source: "recovery_email",
          join_intent: "landing_view",
          cta_surface: "page",
        });
      } else {
        captureEvent("bio_simple_view", {
          ...baseViewProps,
          email_prefilled: false,
          join_intent: "landing_view",
          cta_surface: "page",
        });
      }

      // Server-side persistence (best-effort, non-blocking) — feeds /admin/checkout-funnel
      const phDistinctId = (window as unknown as { posthog?: { get_distinct_id?: () => string } })
        .posthog?.get_distinct_id?.();
      void fetch("/api/track-template-view", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true, // survives navigation
        body: JSON.stringify({
          template: "BioTemplatePremiumSimple",
          template_variant: countryCode === "AR" ? "ar_direct" : "intl_stripe",
          flow_name: countryCode === "AR" ? "exclusive_ar_simple" : "premium_simple",
          // Pathname captures which AR-shareable link drove this view —
          // /acceso (bio Instagram), /unete (Stories), /vip (DMs),
          // /invitacion-premium (canonical). Same template renders for all 4.
          landing_path: typeof window !== "undefined" ? window.location.pathname : undefined,
          country: countryCode,
          language,
          posthog_distinct_id: phDistinctId,
          customer_email: emailFromQuery,
          has_telegram_url: Boolean(resolvedTelegramUrl),
          has_membership_video: Boolean(resolvedMembershipPlaybackId),
          utm_source: urlParams.get("utm_source") ?? undefined,
          utm_medium: urlParams.get("utm_medium") ?? undefined,
          utm_campaign: urlParams.get("utm_campaign") ?? undefined,
        }),
      }).catch(() => {/* fire-and-forget */});
    }

    // Thumbnail se aplica solo como background-image; preload as=image suele disparar
    // avisos de Chromium ("preloaded but not used") porque no hay <img src> que reutilice el fetch.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- captureEvent + deps alineadas con país/URLs del Worker
  }, [
    countryCode,
    language,
    resolvedTelegramUrl,
    resolvedMembershipPlaybackId,
    resolvedFreeTelegramUrl,
  ]);

  // Inicializar tracking de conversiones al cargar la página
  // Captura UTMs, referrer, landing page para análisis posterior
  useEffect(() => {
    initConversionTracking();
  }, []);

  // Bloquear scroll del body (template principal)
  useEffect(() => {
    const origOverflow = document.body.style.overflow;
    const origHeight = document.body.style.height;
    const origPosition = document.body.style.position;
    document.body.style.overflow = "hidden";
    document.body.style.height = "100dvh";
    document.body.style.position = "fixed";
    return () => {
      document.body.style.overflow = origOverflow;
      document.body.style.height = origHeight;
      document.body.style.position = origPosition;
    };
  }, []);

  // Prevenir scroll cuando el modal de email está abierto (crítico para Safari iOS)
  useEffect(() => {
    if (!showEmailModal) return;

    const origOverflow = document.documentElement.style.overflow;
    const origTouchAction = document.documentElement.style.touchAction;
    const origPosition = document.body.style.position;
    const origTop = document.body.style.top;
    const origWidth = document.body.style.width;
    
    // Safari iOS: prevenir scroll del body detrás del modal
    const scrollY = window.scrollY;
    document.documentElement.style.overflow = "hidden";
    document.documentElement.style.touchAction = "none";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";

    return () => {
      // Restaurar scroll
      document.documentElement.style.overflow = origOverflow;
      document.documentElement.style.touchAction = origTouchAction;
      document.body.style.position = origPosition;
      document.body.style.top = origTop;
      document.body.style.width = origWidth;
      window.scrollTo(0, scrollY);
    };
  }, [showEmailModal]);

  // Imagen de fondo: capa md+ usa desktop; capa móvil usa URL móvil o la misma que desktop.
  const resolvedBackgroundDesktopUrl = resolveHttpsBackgroundUrl(
    backgroundImageDesktopUrl ??
      (import.meta.env.VITE_PREMIUM_BACKGROUND_IMAGE_URL as string | undefined),
  );
  const resolvedBackgroundMobileUrl =
    resolveHttpsBackgroundUrl(
      backgroundImageMobileUrl ??
        (import.meta.env.VITE_PREMIUM_BACKGROUND_IMAGE_MOBILE_URL as string | undefined),
    ) ?? resolvedBackgroundDesktopUrl;
  /** Si solo hay URL “móvil”, también se muestra en desktop. */
  const backgroundImageMdPlusUrl =
    resolvedBackgroundDesktopUrl ?? resolvedBackgroundMobileUrl;

  /** Precarga del fondo hero — evita flash feo antes de que el navegador decodifique la foto. */
  const heroBgUrlsToPreload = useMemo(() => {
    const out: string[] = [];
    if (backgroundImageMdPlusUrl) out.push(backgroundImageMdPlusUrl);
    if (
      resolvedBackgroundMobileUrl &&
      resolvedBackgroundMobileUrl !== backgroundImageMdPlusUrl
    ) {
      out.push(resolvedBackgroundMobileUrl);
    }
    return out;
  }, [backgroundImageMdPlusUrl, resolvedBackgroundMobileUrl]);

  const [heroVisualReady, setHeroVisualReady] = useState(
    () => heroBgUrlsToPreload.length === 0,
  );
  const [splashPhase, setSplashPhase] = useState<"hidden" | "visible" | "fading">(
    () => (heroBgUrlsToPreload.length === 0 ? "hidden" : "visible"),
  );

  useEffect(() => {
    if (heroBgUrlsToPreload.length === 0) {
      setHeroVisualReady(true);
      setSplashPhase("hidden");
      return;
    }
    let cancelled = false;
    const preloadOne = (src: string) =>
      new Promise<void>((resolve) => {
        if (typeof Image === "undefined") {
          resolve();
          return;
        }
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = src;
      });
    void Promise.all(heroBgUrlsToPreload.map(preloadOne)).then(() => {
      if (!cancelled) setHeroVisualReady(true);
    });
    const t = window.setTimeout(() => {
      if (!cancelled) setHeroVisualReady(true);
    }, 2600);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [heroBgUrlsToPreload]);

  useEffect(() => {
    if (!heroVisualReady || splashPhase !== "visible") return;
    const id = window.requestAnimationFrame(() => setSplashPhase("fading"));
    return () => window.cancelAnimationFrame(id);
  }, [heroVisualReady, splashPhase]);

  // ── Validación de email ──────────────────────────────────────────────────
  
  const validateEmail = (email: string): boolean => {
    if (!email || !email.trim()) {
      setEmailError(L.emailRequired);
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setEmailError(L.emailInvalid);
      return false;
    }
    
    setEmailError(null);
    return true;
  };

  // ── Click handler para Free Telegram (sin checkout — link directo) ────────
  // Tracking → PostHog + DB + Slack HIGH_INTENT (`bio_simple_free_telegram_click`).
  const handleFreeTelegramClick = useCallback(() => {
    if (typeof window === "undefined") return;
    if (!resolvedFreeTelegramUrl) {
      // No debería pasar — la card sólo se renderiza si la URL está seteada,
      // pero defensivo: trackear el missing para alertar al admin.
      captureEvent("bio_simple_free_telegram_link_missing", {
        env_var: "VITE_TELEGRAM_FREE_GROUP_URL",
        join_intent: "free_telegram_join",
        cta_surface: "free_telegram_card",
      });
      return;
    }
    captureEvent("bio_simple_free_telegram_click", {
      destination: resolvedFreeTelegramUrl,
      free_telegram_url: resolvedFreeTelegramUrl,
      url_configured: true,
      free_telegram_url_source: freeTelegramGroupUrl?.trim()
        ? "prop_freeTelegramGroupUrl"
        : adminFreeTelegramUrl
          ? "admin_free_telegram_url"
          : "env_VITE_TELEGRAM_FREE_GROUP_URL",
      gateway: "none", // no payment
      plan_tier: "free",
      join_intent: "free_telegram_join",
      cta_surface: "free_telegram_card",
    });
    // Abrir en pestaña nueva — los visitantes no abandonan la página de
    // membresía premium (queda visible para evaluar upgrade después).
    window.open(resolvedFreeTelegramUrl, "_blank", "noopener,noreferrer");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedFreeTelegramUrl, freeTelegramGroupUrl, adminFreeTelegramUrl]);

  // ── Checkout de Telegram ──────────────────────────────────────────────────

  const handleTelegramCheckout = async () => {
    if (checkoutState === "loading") return;
    setCheckoutError(null);

    // Stripe-only para membresías. AR pasa por el mismo flujo Stripe Checkout
    // que el resto del mundo (MercadoPago deshabilitado para recurring).
    const trimmedEmail = userEmail.trim();
    if (trimmedEmail && !validateEmail(trimmedEmail)) {
      setShowEmailModal(true);
      return;
    }

    captureEvent("bio_simple_telegram_checkout_click", {
      amount_cents: telegramPlanAmountCents,
      currency: "usd",
      gateway: "stripe",
      has_email: Boolean(trimmedEmail),
      has_identified_email: Boolean(trimmedEmail),
      join_intent: "paid_telegram_basic",
      cta_surface: "telegram_basic_card",
    });

    setCheckoutState("loading");
    setCheckoutError(null);

    try {
      const base = window.location.origin;

      // Construir successUrl: visitantes EN aterrizan en `/portal/complete-profile`;
      // resto en `/portal/completar-perfil` (override por Railway con
      // `VITE_MUCHAS_GRACIAS_SUCCESS_PATH`). El backend valida la suscripción con
      // `session_id` en el handler post-pago.
      const successUrl = buildStripeSuccessReturnUrl(base, uiLanguage);
      const cancelUrl = window.location.href;

      const flowTraceId = crypto.randomUUID();

      captureEvent("bio_simple_checkout_initiated", {
        amount_cents: telegramPlanAmountCents,
        currency_country_derived: true,
        flow_trace_id: flowTraceId,
        has_telegram_url: Boolean(resolvedTelegramUrl),
        ui_mode: "embedded",
        join_intent: "paid_telegram_basic",
        cta_surface: "stripe_embedded_basic",
      });

      // Open the embedded Stripe checkout overlay. Currency is derived
      // server-side from countryCode (Eurozone → EUR, otherwise USD).
      // No more redirect to checkout.stripe.com.
      // Sin priceId — el endpoint resuelve por `plan_tier` desde
      // `membership_products` (admin pega Price ID en /admin/suscripciones).
      setStripeCheckoutConfig({
        amountCents: telegramPlanAmountCents,
        productName: TELEGRAM_PLAN_PRODUCT_NAME,
        country: countryCode,
        buyerEmail: trimmedEmail || undefined,
        returnUrl: successUrl,
        inviteCode: inviteCode || undefined,
        metadata: {
          plan_tier: TELEGRAM_PLAN_TIER,
          flow: TELEGRAM_FLOW_NAME,
          flow_trace_id: flowTraceId,
          product_name: TELEGRAM_PLAN_PRODUCT_NAME,
          country: countryCode,
          template: "BioTemplatePremiumSimple",
          template_variant: "telegram_basic",
          checkout_started_at: new Date().toISOString(),
          ...(trimmedEmail ? { buyer_email: trimmedEmail } : {}),
          ...(vpnDetected ? { vpn_detected: "true" } : {}),
          ...(asOrganization ? { as_organization: asOrganization } : {}),
        },
      });

      // Save the trace id in localStorage so the success page can correlate
      // even if the session_id query param gets dropped on a back-nav.
      try {
        localStorage.setItem(
          "premium_last_stripe_session",
          JSON.stringify({
            flowTraceId,
            createdAt: Date.now(),
          })
        );
      } catch {
        // localStorage bloqueado — no es crítico
      }

      // Reset state so the user can interact with the overlay
      setCheckoutState("idle");
      return;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      console.error("[BioTemplatePremiumSimple] Checkout error:", error);
      captureEvent("bio_simple_checkout_error", {
        error: errorMsg,
        amount_cents: telegramPlanAmountCents,
        currency: "usd",
        join_intent: "paid_telegram_basic",
        cta_surface: "stripe_embedded_basic",
      });
      setCheckoutState("error");
      setCheckoutError(
        error instanceof Error ? error.message : L.telegramError
      );
    }
  };

  // ── Manejador del modal de email ─────────────────────────────────────────
  
  const handleEmailSubmit = () => {
    const email = userEmail.trim();
    
    if (!validateEmail(email)) {
      return;
    }

    identifyUserByEmailPostHog(email, {
      template: "BioTemplatePremiumSimple",
      flow: TELEGRAM_FLOW_NAME,
      landing_path: typeof window !== "undefined" ? window.location.pathname : undefined,
      source: "email_modal",
    });

    captureEvent("bio_simple_email_submitted", {
      has_valid_email: true,
      join_intent: "paid_telegram_basic",
      cta_surface: "email_modal",
    });

    setShowEmailModal(false);
    
    // Reintentar el checkout ahora que tenemos email
    setTimeout(() => {
      handleTelegramCheckout();
    }, 100);
  };

  const handleEmailModalClose = () => {
    setShowEmailModal(false);
    setEmailError(null);
    
    captureEvent("bio_simple_email_modal_closed", {
      had_email: Boolean(userEmail.trim()),
      join_intent: "paid_telegram_basic",
      cta_surface: "email_modal",
    });
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      className="fixed inset-0 z-0 overflow-hidden font-body lining-nums tabular-nums"
      aria-busy={splashPhase === "visible"}
    >
      {/* ── Splash de arranque (solo si hay imagen de fondo configurada) ─────── */}
      {splashPhase !== "hidden" && (
        <div
          className={cn(
            "fixed inset-0 z-[60] flex flex-col items-center justify-center px-6",
            "bg-gradient-to-b from-zinc-950 via-black to-zinc-950",
            "transition-opacity duration-700 ease-out motion-reduce:transition-none",
            splashPhase === "visible"
              ? "opacity-100"
              : "opacity-0 pointer-events-none",
          )}
          aria-hidden={splashPhase !== "visible"}
          onTransitionEnd={(e) => {
            if (e.propertyName !== "opacity") return;
            setSplashPhase((p) => (p === "fading" ? "hidden" : p));
          }}
        >
          <Loader2
            className="h-9 w-9 text-zinc-400 animate-spin mb-5 motion-reduce:animate-none"
            aria-hidden
          />
          <p className="text-sm font-medium text-zinc-300 tracking-wide text-center">
            {L.pageBootstrapping}
          </p>
          <div
            className="mt-10 w-full max-w-[22rem] space-y-3 opacity-55"
            aria-hidden
          >
            <div className="h-[5.5rem] rounded-2xl bg-zinc-800/85 animate-pulse motion-reduce:animate-none" />
            <div className="h-[7rem] rounded-2xl bg-zinc-800/85 animate-pulse motion-reduce:animate-none [animation-delay:150ms]" />
          </div>
        </div>
      )}

      {/* ── Fondo ─────────────────────────────────────────────────────────────── */}
      <div className="absolute inset-0 z-0" aria-hidden>
        <div className="absolute inset-0 bg-black" />
        {backgroundImageMdPlusUrl && (
          <div
            className="absolute inset-0 hidden md:block bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${backgroundImageMdPlusUrl})` }}
          />
        )}
        {resolvedBackgroundMobileUrl && (
          <div
            className="absolute inset-0 md:hidden bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${resolvedBackgroundMobileUrl})` }}
          />
        )}
        {deepBackgroundScrim && (
          <div
            className="absolute inset-0 bg-gradient-to-b from-black/55 via-zinc-950/40 to-black/70"
            aria-hidden
          />
        )}
        <div
          className={cn(
            "absolute inset-0 bg-gradient-to-b",
            deepBackgroundScrim
              ? "from-black/75 via-zinc-950/80 to-black"
              : "from-black/70 via-zinc-950/75 to-black",
          )}
          aria-hidden
        />
        <div
          className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-zinc-900/15"
          aria-hidden
        />
        {deepBackgroundScrim && (
          <div className="absolute inset-0 bg-black/25 pointer-events-none" aria-hidden />
        )}
      </div>

      {/* ── Contenido principal (centrado vertical y horizontal; scroll si no cabe) ── */}
      <div className="absolute inset-0 z-10 overflow-y-auto overflow-x-hidden">
        <div className="min-h-[100dvh] w-full px-3 sm:px-4 flex flex-col items-center justify-center py-10 sm:py-12 md:py-14 pb-[max(2.5rem,env(safe-area-inset-bottom))]">
          {/*
            Columna centrada: mismo ancho en mobile/desktop (max ~390px).
          */}
          <div
            className={cn(
              "w-full max-w-[22rem] sm:max-w-[24rem] md:w-[390px] md:max-w-[390px] flex flex-col items-center text-center mx-auto shrink-0",
              "shadow-[0_24px_80px_-12px_rgba(0,0,0,0.55)]",
            )}
          >

            {/* Botones de acceso */}
            <div className="w-full flex flex-col gap-3.5 sm:gap-4 md:gap-5">

              {/* Card 1: Telegram — AR: Mercado Pago directo; resto: Stripe (email en checkout de Stripe).
                  Visible salvo que el admin (Premium Invite) la desactive. */}
              {showTelegramCard && (
              <div
                className={cn(
                  "p-3.5 sm:p-4 md:p-5 backdrop-blur-md relative w-full min-w-0",
                  "rounded-2xl md:rounded-2xl border border-white/10 bg-zinc-900/65 shadow-xl shadow-black/40",
                )}
              >
                <div className="flex items-center justify-center gap-2 mb-1.5 sm:mb-2">
                  <svg
                    className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0 text-zinc-300"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden
                  >
                    <path d={TELEGRAM_SVG_PATH} />
                  </svg>
                  <span className="text-sm md:text-base tracking-tight font-semibold text-zinc-100">
                    {L.telegramCardTitle}
                  </span>
                </div>
                <p className="text-[11px] sm:text-xs md:text-sm text-center mb-3 sm:mb-3.5 md:mb-4 px-0.5 text-zinc-400 leading-relaxed">
                  {telegramSubtitle}
                </p>

                {/* Error message */}
                {checkoutState === "error" && checkoutError && (
                  <p className="text-red-400 text-[11px] sm:text-xs text-center mb-2 px-1">
                    {checkoutError}
                  </p>
                )}

                <button
                  type="button"
                  disabled={checkoutState === "loading"}
                  onClick={handleTelegramCheckout}
                  className={cn(
                    "flex w-full min-h-[44px] sm:min-h-0 items-center justify-center gap-2 py-2.5 sm:py-3 md:py-3.5 px-3 sm:px-4 md:px-5",
                    "rounded-xl text-sm md:text-base font-semibold transition-all active:scale-[0.98]",
                    "bg-zinc-100 text-zinc-950 hover:bg-white",
                    "shadow-md shadow-black/30",
                    "disabled:opacity-60 disabled:cursor-not-allowed",
                    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-zinc-300",
                  )}
                >
                  {checkoutState === "loading" ? (
                    <>
                      <Loader2 className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0 animate-spin" aria-hidden />
                      {L.telegramLoading}
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden
                      >
                        <path d={TELEGRAM_SVG_PATH} />
                      </svg>
                      {L.telegramCta}
                    </>
                  )}
                </button>
              </div>
              )}

              {/* Card 2: Miembros Exclusivos — muestra thumbnail del video como preview.
                  Visible salvo que el admin (Premium Invite) la desactive. */}
              {showVipCard && (
              <button
                type="button"
                onClick={() => {
                  const vipTouch = "stripe_subscription_vip";
                  if (resolvedMembershipPlaybackId) {
                    captureEvent("bio_simple_exclusive_video_open", {
                      playback_id: resolvedMembershipPlaybackId,
                      destination: vipTouch,
                      join_intent: "vip_membership",
                      cta_surface: "vip_membership_card",
                    });
                    setShowMembershipVideo(true);
                  } else {
                    captureEvent("bio_simple_exclusive_redirect", {
                      destination: vipTouch,
                      reason: "no_video_configured",
                      join_intent: "vip_membership",
                      cta_surface: "vip_membership_card",
                    });
                    void navigateMembershipVipCheckout();
                  }
                }}
                aria-label={L.membershipCta}
                className={cn(
                  "relative w-full min-w-0 overflow-hidden backdrop-blur-[2px] transition-all active:scale-[0.98]",
                  "rounded-2xl md:rounded-2xl border border-white/12 bg-zinc-950/40",
                  "shadow-xl shadow-black/45",
                  "hover:border-white/18 hover:bg-zinc-950/55",
                  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-zinc-500",
                  "text-left",
                )}
                style={{ touchAction: "manipulation" }}
              >
                {/* Thumbnail del video como fondo */}
                {membershipThumbnailUrl ? (
                  <div
                    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                    style={{ backgroundImage: `url(${membershipThumbnailUrl})` }}
                    aria-hidden
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-zinc-900/90 to-black" aria-hidden />
                )}
                {/* Overlay oscuro para legibilidad del texto */}
                <div
                  className="absolute inset-0 bg-gradient-to-b from-black/35 via-zinc-950/55 to-black/88"
                  aria-hidden
                />

                {/* Contenido: flujo normal (no h-full) — garantiza que el botón CTA siempre se ve */}
                <div className="relative z-10 flex flex-col items-center p-3.5 sm:p-4 md:p-5 gap-2 sm:gap-2.5">

                  {/* Header: título + ícono play */}
                  <div className="flex items-center justify-center gap-2 w-full">
                    <Crown
                      className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0 text-zinc-300 drop-shadow"
                      aria-hidden
                    />
                    <span className="text-sm md:text-base drop-shadow font-semibold text-zinc-50">
                      {L.membershipCardTitle}
                    </span>
                    {resolvedMembershipPlaybackId && (
                      <div
                        className="ml-auto flex-shrink-0 w-6 h-6 md:w-7 md:h-7 rounded-full backdrop-blur-sm flex items-center justify-center border border-white/15 bg-white/10"
                      >
                        <svg className="w-3 h-3 md:w-3.5 md:h-3.5 text-white ml-0.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Subtítulo */}
                  <p className="text-[11px] sm:text-xs text-center px-0.5 drop-shadow-sm w-full text-zinc-300 leading-relaxed">
                    {L.membershipCardSubtitle}
                  </p>

                  {/* Botón CTA — siempre visible porque está en flujo normal, no absoluto */}
                  <div
                    className={cn(
                      "flex w-full items-center justify-center gap-2 py-2.5 sm:py-3 px-3 sm:px-4 text-sm md:text-base mt-1",
                      "rounded-xl font-semibold text-zinc-100",
                      "bg-zinc-800/90 border border-white/10",
                      "hover:bg-zinc-800 hover:border-white/16",
                    )}
                  >
                    <Crown className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0 text-zinc-300" aria-hidden />
                    {L.membershipCta}
                  </div>

                </div>
              </button>
              )}

              {/* Card 3: Free Telegram — entrada gratuita al canal abierto.
                  Sólo si `freeTelegramGroupUrl` o `VITE_TELEGRAM_FREE_GROUP_URL` es una URL http(s) válida.
                  Click → analytics + Slack vía bio_simple_free_telegram_click. */}
              {resolvedFreeTelegramUrl && (
                <div
                  className={cn(
                    "p-3.5 sm:p-4 md:p-5 backdrop-blur-md relative w-full min-w-0",
                    "rounded-2xl md:rounded-2xl border border-white/10 bg-zinc-900/45 shadow-lg shadow-black/30",
                  )}
                >
                  <div className="flex items-center justify-center gap-2 mb-1.5 sm:mb-2">
                    <svg
                      className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0 text-zinc-400"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden
                    >
                      <path d={TELEGRAM_SVG_PATH} />
                    </svg>
                    <span className="text-sm md:text-base tracking-tight font-semibold text-zinc-200">
                      {L.freeTelegramCardTitle}
                    </span>
                    <span className="ml-1 inline-flex items-center rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2 py-0.5 text-[10px] md:text-[11px] font-semibold tracking-wide text-emerald-300">
                      Free
                    </span>
                  </div>
                  <p className="text-[11px] sm:text-xs md:text-sm text-center mb-3 sm:mb-3.5 md:mb-4 px-0.5 text-zinc-400 leading-relaxed">
                    {L.freeTelegramCardSubtitle}
                  </p>

                  <button
                    type="button"
                    onClick={handleFreeTelegramClick}
                    className={cn(
                      "flex w-full min-h-[44px] sm:min-h-0 items-center justify-center gap-2 py-2.5 sm:py-3 md:py-3.5 px-3 sm:px-4 md:px-5",
                      "rounded-xl text-sm md:text-base font-semibold transition-all active:scale-[0.98]",
                      "border border-emerald-400/30 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 hover:border-emerald-400/50",
                      "shadow-sm shadow-black/20",
                      "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-400",
                    )}
                    aria-label={L.freeTelegramCta}
                  >
                    <svg
                      className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden
                    >
                      <path d={TELEGRAM_SVG_PATH} />
                    </svg>
                    {L.freeTelegramCta}
                  </button>
                </div>
              )}

            </div>

            {/* Footer */}
            <p className="text-xs md:text-sm mt-5 sm:mt-6 md:mt-8 tracking-wide text-zinc-600 font-normal">
              {BRAND_SITE_HOST}
            </p>

          </div>
        </div>
      </div>

      {/* ── Modal de Email ────────────────────────────────────────────────────── */}
      {showEmailModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85"
          style={{
            // Safari iOS: prevenir scroll del body detrás del modal
            WebkitOverflowScrolling: "touch",
            // Instagram in-app browser: forzar z-index muy alto
            isolation: "isolate",
          }}
          onClick={handleEmailModalClose}
          role="dialog"
          aria-modal="true"
          aria-labelledby="email-modal-title"
        >
          {/* Backdrop blur (separado para mejor compatibilidad) */}
          <div 
            className="absolute inset-0 backdrop-blur-sm"
            style={{ 
              // Fallback para navegadores sin backdrop-blur
              backgroundColor: "rgba(0, 0, 0, 0.3)",
            }}
            aria-hidden
          />

          {/* Modal Content Container - Safe Area para iOS */}
          <div
            className="relative w-full mx-4 sm:mx-auto"
            style={{
              maxWidth: "min(90vw, 420px)",
              // Safe area insets para iPhone con notch
              paddingTop: "env(safe-area-inset-top, 0px)",
              paddingBottom: "env(safe-area-inset-bottom, 0px)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Card */}
            <div
              className="w-full overflow-hidden rounded-2xl sm:rounded-3xl shadow-2xl"
              style={{
                // Gradiente más suave y compatible con Safari
                background: "linear-gradient(180deg, #1e293b 0%, #0f172a 100%)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                // Box shadow con mejor rendering en Safari
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.05)",
              }}
            >
              {/* Header */}
              <div className="relative px-5 sm:px-6 pt-5 sm:pt-6 pb-4">
                <button
                  type="button"
                  onClick={handleEmailModalClose}
                  className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-white/10 transition-all"
                  style={{ WebkitTapHighlightColor: "transparent" }}
                  aria-label={L.emailCancel}
                >
                  <X className="w-5 h-5" />
                </button>

                <div className="flex flex-col items-center text-center pr-8">
                  {/* Icon */}
                  <div
                    className="w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mb-3 sm:mb-4 border border-white/10 bg-zinc-800/80"
                  >
                    <Mail className="w-7 h-7 sm:w-8 sm:h-8 text-zinc-300" />
                  </div>
                  
                  {/* Title */}
                  <h3
                    id="email-modal-title"
                    className="text-xl sm:text-2xl font-bold text-white mb-2 font-body lining-nums tabular-nums"
                  >
                    {L.emailModalTitle}
                  </h3>
                  
                  {/* Subtitle */}
                  <p className="text-sm sm:text-base text-gray-400 leading-relaxed max-w-sm">
                    {L.emailModalSubtitle}
                  </p>
                </div>
              </div>

              {/* Form */}
              <div className="px-5 sm:px-6 pb-5 sm:pb-6">
                {/* Email Input */}
                <div className="mb-5">
                  <label htmlFor="email-input" className="sr-only">
                    Email
                  </label>
                  <input
                    id="email-input"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={userEmail}
                    onChange={(e) => {
                      setUserEmail(e.target.value);
                      setEmailError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && userEmail.trim()) {
                        e.preventDefault();
                        handleEmailSubmit();
                      }
                    }}
                    placeholder={L.emailPlaceholder}
                    className="w-full px-4 py-3.5 sm:py-4 text-base sm:text-lg rounded-xl sm:rounded-2xl text-white placeholder-gray-500 transition-all font-body lining-nums tabular-nums"
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      border: emailError 
                        ? "2px solid rgba(239, 68, 68, 0.5)" 
                        : "2px solid rgba(255, 255, 255, 0.1)",
                      // Safari iOS: prevenir zoom al enfocar input
                      fontSize: "16px",
                      // Instagram in-app: mejor rendering del input
                      WebkitAppearance: "none",
                      appearance: "none",
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = emailError
                        ? "rgba(239, 68, 68, 0.8)"
                        : "rgba(161, 161, 170, 0.55)";
                      e.currentTarget.style.boxShadow = emailError
                        ? "0 0 0 3px rgba(239, 68, 68, 0.15)"
                        : "0 0 0 3px rgba(113, 113, 122, 0.2)";
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = emailError
                        ? "rgba(239, 68, 68, 0.5)"
                        : "rgba(255, 255, 255, 0.1)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                    autoFocus
                  />
                  {emailError && (
                    <div 
                      className="flex items-start gap-2 mt-2.5 px-1"
                      role="alert"
                      aria-live="polite"
                    >
                      <svg 
                        className="w-4 h-4 flex-shrink-0 text-red-400 mt-0.5" 
                        viewBox="0 0 20 20" 
                        fill="currentColor"
                        aria-hidden
                      >
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <span className="text-red-400 text-sm leading-tight">
                        {emailError}
                      </span>
                    </div>
                  )}
                </div>

                {/* Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    onClick={handleEmailModalClose}
                    className="order-2 sm:order-1 sm:flex-1 min-h-[48px] px-5 py-3.5 rounded-xl sm:rounded-2xl font-semibold text-base text-white transition-all"
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 0.06)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      WebkitTapHighlightColor: "transparent",
                      touchAction: "manipulation",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.06)";
                    }}
                  >
                    {L.emailCancel}
                  </button>
                  <button
                    type="button"
                    onClick={handleEmailSubmit}
                    disabled={!userEmail.trim()}
                    className="order-1 sm:order-2 sm:flex-1 min-h-[48px] px-5 py-3.5 rounded-xl sm:rounded-2xl font-semibold text-base text-white transition-all disabled:cursor-not-allowed"
                    style={{
                      background: userEmail.trim() ? "#e4e4e7" : "rgba(63, 63, 70, 0.45)",
                      color: userEmail.trim() ? "#09090b" : "#a1a1aa",
                      opacity: userEmail.trim() ? 1 : 0.65,
                      WebkitTapHighlightColor: "transparent",
                      touchAction: "manipulation",
                      WebkitUserSelect: "none",
                      userSelect: "none",
                    }}
                    onMouseEnter={(e) => {
                      if (userEmail.trim()) {
                        e.currentTarget.style.background = "#fafafa";
                        e.currentTarget.style.transform = "translateY(-1px)";
                        e.currentTarget.style.boxShadow = "0 10px 24px rgba(0, 0, 0, 0.35)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (userEmail.trim()) {
                        e.currentTarget.style.background = "#e4e4e7";
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "none";
                      }
                    }}
                  >
                    {L.emailContinue}
                  </button>
                </div>

                {/* Security note */}
                <p
                  className="text-xs text-gray-500 mt-4 text-center leading-relaxed px-2 font-body lining-nums tabular-nums"
                >
                  {L.emailSecurityNote}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Video Miembros Exclusivos (full-screen overlay) ───────────────────── */}
      {showMembershipVideo && resolvedMembershipPlaybackId && (
        <div
          className="fixed inset-0 z-[9990] flex flex-col w-full h-dvh min-h-dvh overflow-hidden"
          style={{
            backgroundColor: "#000",
            // Thumbnail como fondo del overlay: visible inmediatamente antes de que cargue el video
            ...(membershipThumbnailUrl
              ? {
                  backgroundImage: `url(${membershipThumbnailUrl})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }
              : {}),
          }}
        >
          <MuxVideoPlayerWithCTA
            countryCode={countryCode}
            language={lang}
            playbackId={resolvedMembershipPlaybackId}
            forceManualPlaybackId={true}
            ctaUrl={membershipMuxCtaUrl}
            ctaLabel={L.membershipVideoCta}
            ctaSubtitle={L.membershipVideoCtaSubtitle}
            hideUnmutePrompt={true}
            premiumSimpleMembershipFreeFunnel={!!vipVideoFriendlyFreeDestination}
            muxFriendlyFunnel={muxFunnelOpts}
            containerClassName="flex-1 min-h-0 w-full shrink overflow-hidden"
            onCTAClick={(resolvedUrl) => {
              // Embudo friendly activo → abrir Telegram gratis en vez del checkout VIP.
              if (
                vipVideoFriendlyFreeDestination &&
                (resolvedUrl || "").trim() === vipVideoFriendlyFreeDestination
              ) {
                captureEvent("bio_simple_exclusive_cta_click", {
                  destination: "friendly_free_telegram",
                  source: "video_overlay",
                  join_intent: "free_telegram",
                  cta_surface: "vip_video_overlay",
                });
                window.open(
                  vipVideoFriendlyFreeDestination,
                  "_blank",
                  "noopener,noreferrer",
                );
                setShowMembershipVideo(false);
                return;
              }
              captureEvent("bio_simple_exclusive_cta_click", {
                destination: "stripe_subscription_vip",
                source: "video_overlay",
                join_intent: "vip_membership",
                cta_surface: "vip_video_overlay",
              });
              void navigateMembershipVipCheckout();
            }}
            onPlay={() => setMembershipVideoReady(true)}
            onReady={() => setMembershipVideoReady(true)}
            onClose={() => {
              captureEvent("bio_simple_exclusive_video_closed", {
                join_intent: "vip_membership",
                cta_surface: "vip_video_overlay",
              });
              setShowMembershipVideo(false);
            }}
          />

          {/* Capa de carga amable: poster (blur) + spinner sobre el player
              hasta que el video arranca. pointer-events-none para no robar
              clicks; se desvanece en onPlay/onReady (o timeout de seguridad). */}
          <div
            className={cn(
              "absolute inset-0 z-[1] flex flex-col items-center justify-center gap-4",
              "transition-opacity duration-500 ease-out pointer-events-none",
              membershipVideoReady ? "opacity-0" : "opacity-100",
            )}
            aria-hidden={membershipVideoReady}
          >
            {membershipThumbnailUrl && (
              <div
                className="absolute inset-0 scale-105 blur-[6px] brightness-[0.55]"
                style={{
                  backgroundImage: `url(${membershipThumbnailUrl})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
              />
            )}
            <div className="absolute inset-0 bg-black/45" />
            <Loader2
              className="relative h-9 w-9 text-zinc-200 animate-spin motion-reduce:animate-none"
              aria-hidden
            />
            <span className="relative text-sm font-medium text-zinc-200/90">
              {L.pageBootstrapping}
            </span>
          </div>
        </div>
      )}

      {/* Embedded Stripe checkout — opens for international flows (VIP +
          Telegram basic). AR users redirect to MercadoPago and never reach
          this state. Currency (USD/EUR) is auto-derived server-side from
          countryCode. */}
      <StripeCheckoutOverlay
        open={!!stripeCheckoutConfig}
        config={stripeCheckoutConfig}
        onClose={() => {
          // Mirror overlay close so admins can see where intl users abandon
          captureEvent("bio_simple_checkout_overlay_closed", {
            had_config: !!stripeCheckoutConfig,
            plan_tier: stripeCheckoutConfig?.metadata?.plan_tier,
            close_reason: "user_closed",
          });
          captureEvent("checkout_abandonment", {
            plan_tier: stripeCheckoutConfig?.metadata?.plan_tier,
            abandonment_stage: "stripe_overlay",
            provider: "stripe",
          });
          setStripeCheckoutConfig(null);
        }}
      />
    </div>
  );
}
